import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Briefcase,
  CalendarClock,
  CircleDollarSign,
  Clock,
  FileCheck2,
  Plus,
  StickyNote,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useClientStats } from '@/lib/client-stats';
import { useTimeline, useCreateTimelineEvent } from '@/lib/timeline';
import { useFormError } from '@/lib/form-error';
import { useToast } from '@/components/toast';
import { dollars, formatDate, formatDateLong, formatDateTime, formatPhone } from '@/lib/format';
import type { ClientData, TimelineEventData } from '@/types/api';

export function ClientOverview({ client }: { client: ClientData }) {
  const [logOpen, setLogOpen] = useState(false);
  return (
    <div className="space-y-5">
      <KpiRow clientId={client.id} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="space-y-4 md:col-span-2">
          <RecentActivity clientId={client.id} onLog={() => setLogOpen(true)} />
        </div>
        <div className="space-y-4">
          <DetailsCard client={client} />
          <AddressCard client={client} />
          <QuickActionsCard onLog={() => setLogOpen(true)} />
          {client.notes ? <NotesCard notes={client.notes} /> : null}
        </div>
      </div>

      <LogEventDialog
        clientId={client.id}
        open={logOpen}
        onClose={() => setLogOpen(false)}
      />
    </div>
  );
}

function KpiRow({ clientId }: { clientId: number }) {
  const { data, isLoading } = useClientStats(clientId);
  const tiles: Array<{ label: string; value: string; sub?: string; icon: typeof Wallet; tone?: string }> = [
    {
      label: 'Open balance',
      value: isLoading ? '—' : dollars(data?.open_balance_cents ?? 0),
      sub: 'Unpaid invoices',
      icon: Wallet,
      tone: (data?.open_balance_cents ?? 0) > 0 ? 'text-foreground' : 'text-muted-foreground',
    },
    {
      label: 'Active projects',
      value: isLoading ? '—' : String(data?.active_projects_count ?? 0),
      icon: Briefcase,
    },
    {
      label: 'Open approvables',
      value: isLoading ? '—' : String(data?.open_approvables_count ?? 0),
      sub: 'Awaiting response',
      icon: FileCheck2,
    },
    {
      label: 'Hours this month',
      value: isLoading ? '—' : formatHours(data?.hours_this_month_seconds ?? 0),
      icon: Clock,
    },
    {
      label: 'Last contact',
      value: isLoading ? '—' : data?.last_contact_at ? formatDate(data.last_contact_at) : 'Never',
      sub: data?.last_contact_at ? relativeShort(data.last_contact_at) : undefined,
      icon: CalendarClock,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.label}
            className="rounded-xl border border-border/70 bg-card p-4 shadow-xs"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {t.label}
              </span>
              <Icon className="size-4 text-muted-foreground" />
            </div>
            <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${t.tone ?? ''}`}>
              {t.value}
            </div>
            {t.sub ? (
              <div className="mt-0.5 text-xs text-muted-foreground">{t.sub}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function RecentActivity({
  clientId,
  onLog,
}: {
  clientId: number;
  onLog: () => void;
}) {
  const { data, isLoading } = useTimeline(clientId);
  const top = (data ?? []).slice(0, 6);
  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Recent activity</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Most recent timeline events for this client
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="secondary" onClick={onLog}>
            <Plus className="size-3.5" /> Log
          </Button>
          <Link to={`?tab=timeline`}>
            <Button variant="secondary">
              View all <ArrowRight className="size-3.5" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : top.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
            <Activity className="mx-auto size-5 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No activity logged yet.</p>
            <Button variant="secondary" onClick={onLog} className="mt-3">
              <Plus className="size-3.5" /> Log first event
            </Button>
          </div>
        ) : (
          <ol className="-mx-2 divide-y divide-border/60">
            {top.map((e) => (
              <ActivityRow key={e.id} event={e} />
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function ActivityRow({ event }: { event: TimelineEventData }) {
  return (
    <li className="flex items-start gap-3 px-2 py-2.5">
      <div className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Activity className="size-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="truncate text-sm font-medium">
            {event.title ?? prettyType(event.type)}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(event.occurred_at)}
          </span>
        </div>
        {event.body ? (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{event.body}</p>
        ) : null}
      </div>
    </li>
  );
}

function DetailsCard({ client }: { client: ClientData }) {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Details</h3>
      </CardHeader>
      <CardBody className="space-y-2.5 text-sm">
        <Row label="Website" value={client.website ?? '—'} />
        <Row label="Primary email" value={client.primary_email ?? '—'} />
        <Row label="Primary phone" value={formatPhone(client.primary_phone)} />
        <Row label="Onboarded" value={formatDateLong(client.onboarded_at)} />
      </CardBody>
    </Card>
  );
}

function AddressCard({ client }: { client: ClientData }) {
  const hasAddress =
    client.address_line1 || client.city || client.state || client.postal_code;
  if (!hasAddress) return null;
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Address</h3>
      </CardHeader>
      <CardBody className="space-y-0.5 text-sm text-muted-foreground">
        <div className="text-foreground">{client.address_line1 ?? '—'}</div>
        {client.address_line2 ? <div>{client.address_line2}</div> : null}
        <div>{[client.city, client.state, client.postal_code].filter(Boolean).join(', ') || '—'}</div>
        <div>{client.country}</div>
      </CardBody>
    </Card>
  );
}

function QuickActionsCard({ onLog }: { onLog: () => void }) {
  const actions: Array<{ label: string; to?: string; onClick?: () => void; icon: typeof Plus }> = [
    { label: 'New approvable', to: `?tab=approvables`, icon: FileCheck2 },
    { label: 'New project', to: `?tab=projects`, icon: Briefcase },
    { label: 'Log event', onClick: onLog, icon: StickyNote },
    { label: 'Track time', to: `?tab=time`, icon: Clock },
    { label: 'Bill', to: `?tab=invoices&new=1`, icon: CircleDollarSign },
  ];
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Quick actions</h3>
      </CardHeader>
      <CardBody className="grid grid-cols-2 gap-2">
        {actions.map((a) => {
          const Icon = a.icon;
          const inner = (
            <div className="group/qa flex items-center gap-2 rounded-md border border-border/70 bg-card px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40">
              <Icon className="size-4 text-muted-foreground group-hover/qa:text-primary" />
              <span className="truncate">{a.label}</span>
            </div>
          );
          if (a.to) {
            return (
              <Link key={a.label} to={a.to}>
                {inner}
              </Link>
            );
          }
          return (
            <button key={a.label} type="button" onClick={a.onClick} className="text-left">
              {inner}
            </button>
          );
        })}
      </CardBody>
    </Card>
  );
}

function NotesCard({ notes }: { notes: string }) {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Notes</h3>
      </CardHeader>
      <CardBody className="whitespace-pre-wrap text-sm">{notes}</CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{value ?? '—'}</span>
    </div>
  );
}

function LogEventDialog({
  clientId,
  open,
  onClose,
}: {
  clientId: number;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateTimelineEvent(clientId);
  const toast = useToast();
  const [type, setType] = useState('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pending, setPending] = useState(false);
  const { error, wrap } = useFormError();

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    const ok = await wrap(() => create.mutateAsync({ type, title: title || undefined, body: body || undefined }));
    setPending(false);
    if (ok) {
      toast.success('Event logged');
      setTitle('');
      setBody('');
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Log event">
      <form className="space-y-3" onSubmit={submit}>
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="note">Note</option>
          <option value="call">Call</option>
          <option value="sms">Text</option>
          <option value="email">Email</option>
          <option value="visit">Visit</option>
        </Select>
        <Input
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea
          placeholder="What happened?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Log event'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function formatHours(seconds: number): string {
  if (seconds < 60) return '0h';
  const hours = seconds / 3600;
  if (hours < 10) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours)}h`;
}

function prettyType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

function relativeShort(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const day = Math.floor(diff / 86400000);
  if (day < 1) return 'today';
  if (day === 1) return 'yesterday';
  if (day < 30) return `${day}d ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}
